import { commonUtils, omaSATOIdentityUtils } from '@sato/components';
import axios from 'axios';
import { makeAutoObservable, reaction, runInAction } from 'mobx';

import { api } from '@api/index';
import { SerializedPlainDate } from '@models/api/data.types';
import {
  Contract,
  ContractStatusCode,
  ParkingSpaceContract,
  TokenContract,
} from '@models/contract.types';
import { ParkingProduct } from '@models/parking.types';
import {
  isContractPartyResident,
  isNonContractPartyResident,
} from '@models/Resident';
import { apartmentStore } from '@stores/apartmentStore';
import { authStore } from '@stores/authStore';
import { createOnAuthenticateListener } from '@stores/authStore/authStore.utils';
import { userStore } from '@stores/userStore';
import { RequestQueue } from '@stores/utils/RequestQueue';
import { isQuotaParkingSpace } from '@views/parking/parking.utils';

import {
  createContractRequestId,
  createResidentsRequestId,
  fetchContractWithResidents,
} from './contract.queries';
import {
  getOtherApartmentIdInHomeChange,
  isContractNewOrUpcoming,
  isContractTerminated,
} from './contract.utils';

export class ContractStore {
  contractDataById: Record<string, Contract | null> = {};

  /** contract instances obtained and parsed from idToken cookie */
  private cookieContracts: TokenContract[] | null = null;
  private requestQueue = new RequestQueue();

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });

    // fetch selected contract data on initialization
    createOnAuthenticateListener(this.refreshContracts, this.reset);
  }

  get isFetching() {
    return this.requestQueue.isFetching || this.selectedContract === undefined;
  }

  isFetchingContract(contractId: string | undefined) {
    if (commonUtils.isNullable(contractId)) return false;

    return (
      this.getContract(contractId) === undefined ||
      this.requestQueue.isFetchingId(createContractRequestId(contractId))
    );
  }

  get selectedContractId() {
    return this.getContractByApartmentId(
      apartmentStore.selectedApartmentID ?? undefined
    )?.contractId;
  }

  get selectedContract() {
    if (!this.selectedContractId) return null;

    return this.contractDataById[this.selectedContractId];
  }

  get selectedCookieContract() {
    return this.cookieContracts?.find(
      (contract) => contract.contractId === this.selectedContractId
    );
  }

  get hasContracts() {
    return !!this.cookieContracts?.length;
  }

  get isUserOwnerOfAnyContract() {
    return !!this.cookieContracts?.some(
      (contract) => contract.role === 'PARTY'
    );
  }

  get isApartmentSeeker() {
    return !this.hasContracts;
  }

  get userRole() {
    return this.selectedCookieContract?.role;
  }

  get userSeekerStatus() {
    if (this.isUserOwnerOfAnyContract) {
      return 'contract';
    }
    return 'no_contract';
  }

  get isUserContractParty() {
    return this.selectedCookieContract?.role === 'PARTY';
  }

  /** If the user is not a party in the rental contract */
  get isUserNonContractParty() {
    return this.userRole === 'RESIDENT';
  }

  get residents() {
    return this.selectedContract?.residents;
  }

  /** Get all residents that __are not__ part of the rent contract */
  get nonContractPartyResidents() {
    return this.selectedContract?.residents.filter(isNonContractPartyResident);
  }

  /** Get all residents that __are__ part of the rent contract */
  get contractPartyResidents() {
    return this.selectedContract?.residents.filter(isContractPartyResident);
  }

  /** Contract is new when it is starting and 10 days from the start date */
  get isContractNewOrUpcoming() {
    return isContractNewOrUpcoming(this.selectedContract);
  }

  get isContractTerminated() {
    return isContractTerminated(this.selectedContract);
  }

  /** The user's contracts other than the selected one. Contract data is `undefined` if not fetched. */
  get otherApartmentContracts() {
    return (this.cookieContracts ?? [])
      .filter(({ contractId }) => contractId !== this.selectedContractId)
      .map(({ apartmentId, contractId }) => ({
        apartmentId,
        contractId,
        contract: this.contractDataById[contractId],
      }));
  }

  /** See `getOtherApartmentIdInHomeChange`. Call `fetchOtherApartmentContracts` first. */
  get otherApartmentIdInHomeChange() {
    return getOtherApartmentIdInHomeChange(
      this.selectedContract,
      this.otherApartmentContracts
    );
  }

  get isContractEnded() {
    return (
      this.selectedContract?.statusCodeOfContract ===
      ContractStatusCode.ENDED_A_STATUS
    );
  }

  /**
   * Adds new parking space contract to state using information from parking product and renting form.
   * Useful if we want to optimistically update state before it is actually updated in the backend.
   */
  addQuickRentedParkingContractFromParkingProduct({
    contractId,
    parkingProduct,
    contractStartingDate,
  }: {
    parkingProduct: ParkingProduct;
    contractId: string;
    contractStartingDate: SerializedPlainDate;
  }) {
    const newContract: ParkingSpaceContract = {
      contractStartingDate,
      grossTotal: parkingProduct.rent,
      isQuotaPlace: isQuotaParkingSpace(parkingProduct.parkingSpace),
      parkingContractId: contractId,
      parkingSpaceId: parkingProduct.parkingSpace.id,
      parkingSpaceNumber: parkingProduct.parkingSpace.labelId,
      parkingSpacetype: parkingProduct.parkingSpace.type,
      realEstateId: parkingProduct.parkingArea.realEstateId,
      address: parkingProduct.parkingArea.address,
      parkingProductId: parkingProduct.id,
      isQuickRent: true,
      // Quick rented parking products should always be indefinite (toistaiseksi voimassa-oleva)
      parkingContractType: 'INDEFINITELY_TYPE',
      contractEndDate: null,
      contractStatus: ContractStatusCode.STARTING_STATUS,
      hasTerminationInformationText: false,
      rentingParty: parkingProduct.parkingArea.parkingRentingParty,
      parkingArea: parkingProduct.parkingArea,
      parkingSpace: parkingProduct.parkingSpace,
      renterContactIds: [userStore.user!.crmID],
    };

    runInAction(() => {
      if (!this.selectedContract) throw Error('No contract was selected!');

      this.selectedContract.parkingSpaces = [
        ...(this.selectedContract.parkingSpaces ?? []),
        newContract,
      ];
    });
  }

  /**
   * Manually sets parking contract as terminated in stored data.
   * Useful if we want to optimistically update state before it is actually updated in the backend.
   */
  setParkingSpaceContractAsTerminated({
    contractEndDate,
    contractId,
  }: {
    contractId: string;
    contractEndDate: SerializedPlainDate;
  }) {
    runInAction(() => {
      if (!this.selectedContract) throw Error('No contract was selected!');

      const contractToChange = this.selectedContract?.parkingSpaces?.find(
        ({ parkingContractId }) => parkingContractId === contractId
      );

      const otherContracts = this.selectedContract.parkingSpaces?.filter(
        ({ parkingContractId }) => parkingContractId !== contractId
      );

      if (!contractToChange) return;

      this.selectedContract.parkingSpaces = [
        ...(otherContracts ?? []),
        {
          ...contractToChange,
          contractEndDate,
          contractStatus: ContractStatusCode.RESIGNED_STATUS,
        },
      ];
    });
  }

  getContract(contractId: string | undefined) {
    if (commonUtils.isNullable(contractId)) return null;

    return this.contractDataById[contractId];
  }

  getContractByApartmentId(apartmentId: string | undefined) {
    if (commonUtils.isNullable(apartmentId)) return undefined;

    return this.cookieContracts?.find(
      (contract) => contract.apartmentId === apartmentId
    );
  }

  isUserOwnerOfApartment(apartmentId: string) {
    return this.getContractByApartmentId(apartmentId)?.role === 'PARTY';
  }

  isUserContractPartyInContract(contractId: string) {
    return (
      this.cookieContracts?.find(
        (contract) => contract.contractId === contractId
      )?.role === 'PARTY'
    );
  }

  isUserNamedInParkingSpaceContract(customerContactId: string | undefined) {
    if (!customerContactId) return false;

    return this.selectedContract?.parkingSpaces?.some((space) =>
      space?.renterContactIds?.includes(customerContactId)
    );
  }

  updateParkingContractAsTerminationPending(
    parkingContractId: string,
    terminationDate: SerializedPlainDate
  ) {
    if (commonUtils.isNullable(parkingContractId)) return;

    runInAction(() => {
      const parkingSpace = this.selectedContract?.parkingSpaces?.find(
        (space) => space.parkingContractId === parkingContractId
      );

      if (parkingSpace) {
        parkingSpace.contractStatus = ContractStatusCode.TERMINATION_PENDING;
        parkingSpace.contractEndDate = terminationDate;
      }
    });
  }

  /** Fetch contract data for the user's other apartments, since only the selected contract is fetched by default */
  fetchOtherApartmentContracts() {
    // `otherApartmentIdInHomeChange` doesn't need contract data when there is only one other contract
    if (this.otherApartmentContracts.length <= 1) return;

    this.otherApartmentContracts
      .filter(({ contract }) => contract === undefined)
      .forEach(({ contractId }) => this.fetchContract(contractId));
  }

  /** Update contracts from cookie */
  refreshContracts() {
    this.cookieContracts =
      omaSATOIdentityUtils.getUserDataFromIdToken()?.contracts ?? [];

    // refecth currently selected contract
    this.fetchContract(this.selectedContractId);

    // reset apartment selection if it's not included in the contracts
    if (
      this.cookieContracts.every(
        ({ apartmentId }) => apartmentId !== apartmentStore.selectedApartmentID
      )
    ) {
      apartmentStore.setSelectedApartmentID(null);
    }
  }

  /** Utility function for checking whether the current user can query contract data with the given payload */
  private isValidContractRequestConditions(
    contractId: string | undefined
  ): contractId is string {
    if (
      // ... contract ID was not given (nothing to be fetched)
      commonUtils.isNullable(contractId) ||
      // ... contract was fetched previously but returned no data (404 response)
      this.contractDataById[contractId] === null ||
      // ... user is not a contract party in the requested contract (the request will fail with 404)
      !this.isUserContractPartyInContract(contractId)
    ) {
      // if the user is not a contract party, set contract data for the requested ID `null`
      if (contractId && !this.isUserContractPartyInContract(contractId)) {
        this.contractDataById[contractId] = null;
      }

      return false;
    }

    return true;
  }

  async fetchContract(contractId: string | undefined) {
    if (!this.isValidContractRequestConditions(contractId)) return;

    return this.requestQueue.fetch(
      createContractRequestId(contractId),
      async () => {
        try {
          const data = await fetchContractWithResidents(contractId);

          runInAction(() => {
            this.contractDataById[contractId] = data;
          });

          return data;
        } catch (err) {
          // set contract data as null if no contract data was found
          if (axios.isAxiosError(err) && err.response?.status === 404) {
            runInAction(() => {
              this.contractDataById[contractId] = null;
            });

            return null;
          }

          throw err;
        }
      },
      { ignoreCache: true }
    );
  }

  /** Utility for refreshing resident data on contract, for example, after submitting
   * changes to resident data */
  async refreshResidents(contractId: string) {
    const contract = this.contractDataById[contractId];

    if (
      !this.isValidContractRequestConditions(contractId) ||
      // ensure that the requested contract exists
      !contract
    ) {
      return;
    }

    return this.requestQueue.fetch(
      createResidentsRequestId(contractId),
      async () => {
        const response = await api.residents.fetchResidents(contractId);

        runInAction(() => {
          contract.residents = response.data;
        });

        return response;
      },
      { ignoreCache: true }
    );
  }

  /** Reset contract store state to initial state, for example, in logout */
  reset() {
    this.cookieContracts = null;
    this.contractDataById = {};
    this.requestQueue.clear();
  }
}

export const contractStore = new ContractStore();

// trigger contract refresh on cookies update
reaction(() => authStore.idToken, contractStore.refreshContracts);

// fetch contract data on apartment selection change
reaction(
  // trigger reaction on both selected apartment change and contracts update
  () => apartmentStore.selectedApartmentID,
  (apartmentId) => {
    if (!apartmentId) return;

    const contractId =
      contractStore.getContractByApartmentId(apartmentId)?.contractId;

    if (commonUtils.isNullable(contractId)) return;

    contractStore.fetchContract(contractId);
  }
);
